import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message, Modal, theme, Typography } from "antd";
import { useState } from "react";
import { MIME_TYPE_MERGE_PATCH_JSON } from "@rumble-it/common-lib/src/constants/mimeTypes";
import {
  useViewConfigurationAffiliateId,
  useViewConfigurationClient,
} from "../client";
import type { LocationOption } from "./LocationSelectorCard";

export interface HandInLocationSelectorCardProps {
  title: string;
  emptyState: string;
  submitEndpoint: string;
  submitField: string;
  submitAddressField: string;
  selectedName: string;
  selectedAddress: string;
}

const formatAddress = (address?: LocationOption["address"]) => {
  if (!address) return "";
  return [
    [address.street, address.streetNumber, address.streetNumberAddition]
      .filter(Boolean)
      .join(" "),
    [address.postalCode, address.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
};

// Always offers both service partners and dealerships; may support a custom
// hand-in address in the future.
export const HandInLocationSelectorCard = ({
  title,
  emptyState,
  submitEndpoint,
  submitField,
  submitAddressField,
  selectedName,
  selectedAddress,
}: HandInLocationSelectorCardProps) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();
  const [optionsVisible, setOptionsVisible] = useState(false);
  const { token } = theme.useToken();

  const affiliateId = useViewConfigurationAffiliateId();

  const servicePartnersQuery = useQuery({
    queryKey: ["servicePartners", affiliateId],
    queryFn: async (): Promise<LocationOption[]> =>
      axiosInstance.get("/service-partners").then((response) => response.data),
    enabled: optionsVisible,
  });

  const dealershipsQuery = useQuery({
    queryKey: ["dealerships", affiliateId],
    queryFn: async (): Promise<LocationOption[]> =>
      axiosInstance
        .get("/companies?type=dealership")
        .then((response) => response.data),
    enabled: optionsVisible,
  });

  const submitMutation = useMutation({
    mutationFn: async (option: LocationOption) =>
      axiosInstance.patch(
        submitEndpoint,
        {
          [submitField]: option.name || option.fullName || null,
          [submitAddressField]: {
            street: option.address?.street || null,
            streetNumber: option.address?.streetNumber || null,
            streetNumberAddition: option.address?.streetNumberAddition || null,
            postalCode: option.address?.postalCode || null,
            city: option.address?.city || null,
            country: "NL",
          },
        },
        { headers: { "Content-Type": MIME_TYPE_MERGE_PATCH_JSON } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
      setOptionsVisible(false);
    },
    onError: () => {
      message.error("Er is een fout opgetreden. Probeer het opnieuw.");
    },
  });

  const options = [
    ...(servicePartnersQuery.data ?? []),
    ...(dealershipsQuery.data ?? []),
  ];

  const isLoading =
    servicePartnersQuery.isLoading || dealershipsQuery.isLoading;

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          <Button
            type="link"
            style={{ padding: 0, fontWeight: "normal" }}
            onClick={() => setOptionsVisible(true)}
          >
            wijzigen
          </Button>
        </div>
      }
    >
      {selectedName ? (
        <div>
          <Typography.Text strong>{selectedName}</Typography.Text>
          {selectedAddress && (
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {selectedAddress}
            </Typography.Paragraph>
          )}
        </div>
      ) : (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {emptyState}
        </Typography.Paragraph>
      )}

      <Modal
        title={`${title} wijzigen`}
        open={optionsVisible}
        onCancel={() => setOptionsVisible(false)}
        footer={null}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {isLoading && <Card loading />}
          {!isLoading &&
            options.map((option) => {
              const isSelected =
                (option.name || option.fullName) === selectedName;
              const isOptionPending =
                submitMutation.isPending &&
                submitMutation.variables?.["@id"] === option["@id"];

              return (
                <div
                  key={option["@id"]}
                  style={{
                    border: isSelected
                      ? `1px solid ${token.colorPrimary}`
                      : `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  <Typography.Text strong>
                    {option.name || option.fullName}
                  </Typography.Text>
                  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                    {formatAddress(option.address)}
                  </Typography.Paragraph>
                  <Button
                    type={isSelected ? "primary" : "default"}
                    style={{ marginTop: 8, width: "100%" }}
                    loading={isOptionPending}
                    disabled={submitMutation.isPending && !isOptionPending}
                    onClick={() => submitMutation.mutate(option)}
                  >
                    {option["@type"] === "Company"
                      ? "Dealer kiezen"
                      : "Hersteller kiezen"}
                  </Button>
                </div>
              );
            })}
        </div>
      </Modal>
    </Card>
  );
};
